import { PDFDocument, type PDFFont, rgb, type Color } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { retrieveJSONFromSession } from '@/utils/storage';
import { defaultROIReportValues } from '@/components/ROICalculator/ROICalculator';

// Location on the PDF to place text
type Location = {
  pageNumber: number;
  x: number;
  y: number;
};

// Options for drawing text on the PDF
type TextOptions = {
  locations: Location[];
  values: string[];
  size: number;
  font: PDFFont;
  color: Color;
  lineHeight?: number;
};

// This iteration works for USD/english with a dataset of 5 years
// and will need to be updated if these change

// RGB color values for the primary text color (dark purple)
const PRIMARY_TEXT_COLOR_RED = 0.16;
const PRIMARY_TEXT_COLOR_GREEN = 0;
const PRIMARY_TEXT_COLOR_BLUE = 0.26;

export async function markupPDF() {
  // get our stored user input and calculations
  const storedData = retrieveJSONFromSession('ROI_CALCULATOR');
  const {
    chartArgs: { values, hoursSaved, costSaved },
    chartInputs: { engineerCount, engineerSalary },
    userInfo: { companyName },
  } = mergeWithDefaults(storedData, defaultROIReportValues);

  // load the unmarked pdf
  const unmarkedPdfUrl = '/assets/roi.pdf';
  const unmarkedPdfBytes = await fetch(unmarkedPdfUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(unmarkedPdfBytes);

  // embed fonts
  pdfDoc.registerFontkit(fontkit);
  const fonts = {
    interBold: await embedFont(pdfDoc, '/fonts/inter/inter-v20-latin-700.ttf'),
    poppins: await embedFont(pdfDoc, '/fonts/poppins/poppins-v24-latin-400.ttf'),
    poppinsBold: await embedFont(pdfDoc, '/fonts/poppins/poppins-v24-latin-600.ttf'),
    robotoMonoRegular: await embedFont(pdfDoc, '/fonts/roboto-mono-v31-latin-400.ttf'),
  };

  const MONO_CHAR_PIXEL_OFFSET = 5;
  const color: Color = rgb(
    PRIMARY_TEXT_COLOR_RED,
    PRIMARY_TEXT_COLOR_GREEN,
    PRIMARY_TEXT_COLOR_BLUE
  );
  const breakEarly = companyName.length + formatNumber(engineerCount).length > 16;
  const startingSentence = `Based on your team size of ${formatNumber(engineerCount)} developers, here are the potential productivity gains${breakEarly ? '\n' : ' '}${companyName}${breakEarly ? ' ' : ' \n'}could see after adopting SonarQube.`;
  const initialChartPositions = [
    // chart values
    { pageNumber: 3, x: 250, y: 1115 },
    { pageNumber: 3, x: 430, y: 1170 },
    { pageNumber: 3, x: 610, y: 1243 },
    { pageNumber: 3, x: 790, y: 1328 },
    { pageNumber: 3, x: 970, y: 1395 },
  ];

  // center digits above the bar chart
  // we need to move the starting position of the value based on the digit count
  const centeredValues = values.map((value: number, index: number) => {
    const { x, ...rest } = initialChartPositions[index];
    const LOWER_CHAR_LIMIT = 6; // 4 digits 1 comma 1 currency
    let offset = 0;
    const chars = formatCurrency(value).toString().length;
    offset += chars - LOWER_CHAR_LIMIT;
    offset *= MONO_CHAR_PIXEL_OFFSET;

    return {
      x: x - offset,
      ...rest,
    };
  });

  // draw all text options
  const drawTextOptions = [
    {
      locations: [
        { pageNumber: 1, x: 443, y: 1690 }, // companyName in top margins
        { pageNumber: 2, x: 443, y: 1690 }, // *
        { pageNumber: 3, x: 443, y: 1690 }, // *
      ],
      values: [companyName, companyName, companyName],
      size: 17,
      font: fonts.interBold,
      color,
    },
    {
      locations: [
        { pageNumber: 1, x: 80, y: 1507 }, // starting sentence
      ],
      values: [startingSentence],
      size: 24,
      font: fonts.interBold,
      color,
    },
    {
      locations: [
        { pageNumber: 1, x: 82, y: 1320 }, // hoursSaved
        { pageNumber: 1, x: 482, y: 1320 }, // costSaved
        { pageNumber: 1, x: 885, y: 1320 }, // 5 year total
      ],
      values: [
        formatNumber(hoursSaved),
        formatCurrency(costSaved),
        formatCurrency(values.at(-1) ?? 0),
      ],
      size: 28,
      font: fonts.poppinsBold,
      color,
    },
    {
      locations: [
        { pageNumber: 2, x: 80, y: 580 }, // engineerCount
        { pageNumber: 2, x: 700, y: 580 }, // engineerSalary
      ],
      values: [formatNumber(engineerCount), formatCurrency(engineerSalary)],
      size: 24,
      font: fonts.poppinsBold,
      color,
    },
    {
      locations: centeredValues, // chart values
      values: values.map(formatCurrency),
      size: 16,
      font: fonts.robotoMonoRegular,
      color: rgb(0, 0, 0),
      opacity: 1,
    },
    {
      locations: [
        { pageNumber: 3, x: 245, y: 927 }, // hoursSaved
        { pageNumber: 3, x: 516, y: 927 }, // costSaved
      ],
      values: [formatNumber(hoursSaved), formatCurrency(costSaved)],
      size: 33,
      font: fonts.poppinsBold,
      color,
    },
  ];

  for (const option of drawTextOptions) {
    draw(pdfDoc, option);
  }

  const pdfBytes = (await pdfDoc.save()) as Uint8Array<ArrayBuffer>;
  const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
  return pdfBlob;
}

function draw(pdfDoc: PDFDocument, textOptions: TextOptions) {
  const pages = pdfDoc.getPages();
  const { locations, values, size, font, color, lineHeight = 38 } = textOptions;

  for (let i = 0; i < locations.length; i++) {
    const { pageNumber, x, y } = locations[i];
    const value = values[i];
    if (value === undefined) {
      console.log(`value ${i} is undefined`);
      continue;
    }
    const page = pages[pageNumber - 1];
    page.drawText(value.toString(), {
      x,
      y,
      size,
      font,
      color,
      lineHeight,
    });
  }
}

async function embedFont(pdfDoc: PDFDocument, url: string) {
  const fontBytes = await fetch(url).then((res) => res.arrayBuffer());
  return pdfDoc.embedFont(fontBytes);
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    maximumFractionDigits: 0,
    currency: 'USD',
  });
}

// Deep merge helper to safely merge stored data with defaults
function mergeWithDefaults(
  stored: Partial<typeof defaultROIReportValues>,
  defaults: typeof defaultROIReportValues
): typeof defaultROIReportValues {
  const NUM_OF_YEARS = 5;

  const storedData = stored || {};
  const defaultValues = [0, 0, 0, 0, 0];

  // Determine which values array to use - prefer stored data if valid, then defaults, then fallback
  const getValuesArray = () => {
    const storedValues = storedData.chartArgs?.values;
    const defaultValuesArray = defaults.chartArgs.values;

    if (storedValues && storedValues.length >= NUM_OF_YEARS) {
      return storedValues;
    }

    if (defaultValuesArray && defaultValuesArray.length >= NUM_OF_YEARS) {
      return defaultValuesArray;
    }

    return defaultValues;
  };

  return {
    chartArgs: {
      ...defaults.chartArgs,
      ...storedData.chartArgs,
      // Ensure values array exists and has the correct number of entries
      values: getValuesArray(),
    },
    chartInputs: {
      ...defaults.chartInputs,
      ...storedData.chartInputs,
    },
    userInfo: {
      ...defaults.userInfo,
      ...storedData.userInfo,
    },
  };
}
